import { getRuntimeApiUrl } from './runtimeConfig'

const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

export const BRANDING_CACHE_KEY = 'app-branding:v1'

export interface Branding {
  companyName: string
  primaryColor: string
  accentColor: string
  /** Absolute URL of the custom logo, or null when the default logo applies. */
  logoSrc: string | null
  version: string
}

export const DEFAULT_BRANDING: Branding = {
  companyName: 'Procurement Command Center',
  primaryColor: '#141A42',
  accentColor: '#8AA2DF',
  logoSrc: null,
  version: '0',
}

export const PRIMARY_PRESETS = [
  '#850C20',
  '#1D4ED8',
  '#7C3AED',
  '#059669',
  '#D97706',
  '#0F172A',
  '#E11D48',
  '#0891B2',
]

export const ACCENT_PRESETS = [
  '#16865D',
  '#3B82F6',
  '#A855F7',
  '#DC2626',
  '#F97316',
  '#0891B2',
  '#65A30D',
  '#DB2777',
]

export interface BrandingApiResponse {
  company_name: string
  primary_color: string
  accent_color: string
  logo_url: string | null
  version: string
}

export const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/

export async function toBranding(data: BrandingApiResponse): Promise<Branding> {
  const apiUrl = data.logo_url ? await getRuntimeApiUrl() : ''
  return {
    companyName: data.company_name,
    primaryColor: data.primary_color,
    accentColor: data.accent_color,
    logoSrc: data.logo_url ? `${apiUrl}${BASE_PATH}${data.logo_url}` : null,
    version: data.version,
  }
}

/** Public (unauthenticated) read so the login screen can be branded too. */
export async function fetchPublicBranding(): Promise<Branding> {
  const apiUrl = await getRuntimeApiUrl()
  const response = await fetch(`${apiUrl}${BASE_PATH}/api/branding/public`)
  if (!response.ok) throw new Error(`Branding request failed: ${response.status}`)
  return toBranding((await response.json()) as BrandingApiResponse)
}

function hexToRgb(hex: string): [number, number, number] {
  const value = parseInt(hex.slice(1), 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

function rgbTriplet([r, g, b]: [number, number, number]): string {
  return `${r} ${g} ${b}`
}

function rgbToHsl([r, g, b]: [number, number, number]): string {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  const d = max - min
  let h = 0
  let s = 0
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1))
    if (max === rn) h = ((gn - bn) / d) % 6
    else if (max === gn) h = (bn - rn) / d + 2
    else h = (rn - gn) / d + 4
    h = Math.round(h * 60)
    if (h < 0) h += 360
  }
  return `${h} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

function isLight([r, g, b]: [number, number, number]): boolean {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6
}

function mix(
  a: [number, number, number],
  b: [number, number, number]
): [number, number, number] {
  return [
    Math.round((a[0] + b[0]) / 2),
    Math.round((a[1] + b[1]) / 2),
    Math.round((a[2] + b[2]) / 2),
  ]
}

const DOCUMENT_TITLE_SUFFIX = 'CapEx Tendering & Feasibility Management'

const FAVICON_SIZE = 64
/** Logos wider than this are treated as "mark + wordmark" and cropped to the left square. */
const WIDE_LOGO_RATIO = 1.6
const faviconCache = new Map<string, string>()

const originalTabIcons = new Map<HTMLLinkElement, string>()

function tabIconLinks(): HTMLLinkElement[] {
  return Array.from(
    document.querySelectorAll<HTMLLinkElement>("link[rel='icon'], link[rel='apple-touch-icon']")
  )
}

function setTabIcon(href: string): void {
  tabIconLinks().forEach((link) => {
    if (!originalTabIcons.has(link)) originalTabIcons.set(link, link.href)
    link.href = href
  })
}

/** No logo uploaded: put back whatever icon the page shipped with. */
function restoreTabIcon(): void {
  originalTabIcons.forEach((href, link) => {
    link.href = href
  })
}

/**
 * A tab icon is tiny and square, so a wide logo is squashed unreadable. Wide logos
 * are cropped to their left square (the mark); other logos are fitted with padding.
 */
async function buildSquareFavicon(src: string): Promise<string | null> {
  const cached = faviconCache.get(src)
  if (cached) return cached
  try {
    const response = await fetch(src)
    if (!response.ok) return null
    const bitmap = await createImageBitmap(await response.blob())
    const canvas = document.createElement('canvas')
    canvas.width = FAVICON_SIZE
    canvas.height = FAVICON_SIZE
    const context = canvas.getContext('2d')
    if (!context) return null

    if (bitmap.width / bitmap.height > WIDE_LOGO_RATIO) {
      context.drawImage(bitmap, 0, 0, bitmap.height, bitmap.height, 0, 0, FAVICON_SIZE, FAVICON_SIZE)
    } else {
      const scale = Math.min(FAVICON_SIZE / bitmap.width, FAVICON_SIZE / bitmap.height)
      const width = bitmap.width * scale
      const height = bitmap.height * scale
      context.drawImage(bitmap, (FAVICON_SIZE - width) / 2, (FAVICON_SIZE - height) / 2, width, height)
    }
    const dataUrl = canvas.toDataURL('image/png')
    faviconCache.set(src, dataUrl)
    return dataUrl
  } catch {
    return null
  }
}

/** Browser tab: icon follows the logo, title follows the company name. */
function applyBrandingToTab(branding: Branding): void {
  document.title = `${branding.companyName} | ${DOCUMENT_TITLE_SUFFIX}`
  const { logoSrc } = branding
  if (!logoSrc) {
    restoreTabIcon()
    return
  }
  const cached = faviconCache.get(logoSrc)
  if (cached) {
    setTabIcon(cached)
    return
  }
  void buildSquareFavicon(logoSrc).then((icon) => setTabIcon(icon ?? logoSrc))
}

type Rgb = [number, number, number]

function shade(color: Rgb, toward: Rgb, amount: number): Rgb {
  return [
    Math.round(color[0] + (toward[0] - color[0]) * amount),
    Math.round(color[1] + (toward[1] - color[1]) * amount),
    Math.round(color[2] + (toward[2] - color[2]) * amount),
  ]
}

const WHITE: Rgb = [255, 255, 255]
const BLACK: Rgb = [0, 0, 0]

function rgbHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

/** CSS variables the theme (globals.css) defines per theme; branding overrides them inline. */
function brandingVariables(primary: Rgb, accent: Rgb): Record<string, string> {
  const mid = mix(primary, accent)
  const [p, m, a] = [rgbHex(primary), rgbHex(mid), rgbHex(accent)]
  return {
    '--primary': rgbToHsl(primary),
    '--primary-foreground': isLight(primary) ? '222 47% 11%' : '0 0% 100%',
    '--accent-foreground': rgbToHsl(primary),
    '--ring': rgbToHsl(primary),
    '--chart-1': rgbToHsl(accent),
    '--brand-navy': rgbTriplet(primary),
    '--brand-navy-hover': rgbTriplet(shade(primary, WHITE, 0.12)),
    '--brand-navy-light': rgbTriplet(shade(primary, WHITE, 0.2)),
    '--brand-navy-lighter': rgbTriplet(shade(primary, WHITE, 0.28)),
    '--brand-navy-dark': rgbTriplet(shade(primary, BLACK, 0.15)),
    '--brand-cornflower': rgbTriplet(accent),
    '--brand-purple': rgbTriplet(mid),
    '--text-link': rgbTriplet(mid),
    '--text-link-hover': rgbTriplet(accent),
    '--accent-rgb': rgbTriplet(accent),
    '--primary-300': rgbTriplet(shade(accent, WHITE, 0.25)),
    '--primary-400': rgbTriplet(shade(accent, WHITE, 0.1)),
    '--primary-500': rgbTriplet(accent),
    '--primary-600': rgbTriplet(accent),
    '--primary-700': rgbTriplet(shade(accent, BLACK, 0.12)),
    '--brand-shadow-rgb': rgbTriplet(primary),
    '--brand-gradient': `linear-gradient(135deg, ${p} 0%, ${m} 100%)`,
    '--brand-hero': `linear-gradient(to bottom right, ${p}, ${rgbHex(shade(primary, accent, 0.25))}, ${m})`,
    '--brand-text-gradient': `linear-gradient(135deg, ${p} 0%, ${m} 50%, ${a} 100%)`,
  }
}

/**
 * Applies saved brand colours over the active theme. While the colours are still
 * the defaults nothing is overridden, so each theme (e.g. Adani) keeps its own palette.
 */
export function applyBrandingToDocument(branding: Branding): void {
  if (typeof document === 'undefined') return
  applyBrandingToTab(branding)
  if (
    !HEX_COLOR_REGEX.test(branding.primaryColor) ||
    !HEX_COLOR_REGEX.test(branding.accentColor)
  ) {
    return
  }
  const primary = hexToRgb(branding.primaryColor)
  const accent = hexToRgb(branding.accentColor)
  const root = document.documentElement.style
  const isDefault =
    branding.primaryColor.toUpperCase() === DEFAULT_BRANDING.primaryColor &&
    branding.accentColor.toUpperCase() === DEFAULT_BRANDING.accentColor

  for (const [name, value] of Object.entries(brandingVariables(primary, accent))) {
    if (isDefault) root.removeProperty(name)
    else root.setProperty(name, value)
  }
}

export function readCachedBranding(): Branding | null {
  try {
    const raw = window.localStorage.getItem(BRANDING_CACHE_KEY)
    return raw ? (JSON.parse(raw) as Branding) : null
  } catch {
    return null
  }
}

export function writeCachedBranding(branding: Branding): void {
  try {
    window.localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(branding))
  } catch {
    // Storage unavailable (private mode / quota): branding still applies for this session.
  }
}
